/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module DnsModule
 */

import {
    IDnsQueryNameMatch,
    StringOperation,
} from 'generated-types';

import { DnsQueryNameMatch } from 'object-types';

import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';
import { SchemaService } from 'ajs/modules/core/services/schema-service/schema.service';
import { StringService } from 'string-service';

/**
 * @description  DnsQueryNameMatch ConfigItem class.
 * @author Abhinesh Gour
 */
export class DnsQueryNameMatchConfigItem extends MessageItem<IDnsQueryNameMatch> {
    public static ajsDependencies = [
        'schemaService',
        'stringService',
    ];

    private readonly schemaService: SchemaService;
    private readonly stringService: StringService;

    constructor(args = {}) {
        super({
            objectType: DnsQueryNameMatch,
            ...args,
        });

        this.schemaService = this.getAjsDependency_('schemaService');
        this.stringService = this.getAjsDependency_('stringService');
    }

    /**
     * Convenience method to get text label for match criteria.
     */
    public get matchCriteria(): string {
        return this.schemaService.getEnumValueLabel(
            'StringOperation',
            this.config.match_criteria,
        );
    }

    /**
     * Returns String Group Names array for string_group_refs.
     */
    public get stringGroupNames(): string[] {
        const { string_group_refs: stringGroupRefs = [] } = this.config;

        return stringGroupRefs.map(stringGroupRef => this.stringService.name(stringGroupRef));
    }

    /**
     * Returns Query Domain Names array.
     */
    public get domainNames(): string[] {
        const { query_domain_names: domainNames = [] } = this.config;

        return domainNames;
    }

    /** @override */
    // eslint-disable-next-line no-underscore-dangle
    public get defaultConfigOverride_(): Partial<IDnsQueryNameMatch> {
        const defaultValues = this.getAjsDependency_('defaultValues');
        const defaultConfig = defaultValues.getDefaultItemConfigByType(this.objectType) || {};

        return {
            ...defaultConfig,
            match_criteria: StringOperation.BEGINS_WITH,
        };
    }
}
